import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ArrowLeft, Printer, Download, Save } from "lucide-react";
import ReportSheet from "../components/ReportSheet";

const TEMPLATES = {
  progress: [
    "Shows good progress in speaking and listening.",
    "Participates actively in classroom activities.",
    "Needs more practice in vocabulary and writing.",
    "Demonstrates a positive attitude toward learning English.",
    "Should practice English regularly at home.",
  ],
  strengths: [
    "Has strong reading and comprehension skills.",
    "Memorizes new vocabulary quickly.",
    "Speaks confidently in front of the class.",
    "Writes neatly and accurately.",
    "Works well together with classmates.",
  ],
  areas: [
    "Needs to improve pronunciation.",
    "Should build a wider vocabulary.",
    "Needs more practice in grammar and structure.",
    "Should focus more during lessons.",
    "Needs to improve writing accuracy.",
  ],
  recommendation: [
    "Read English books regularly at home.",
    "Practice speaking English every day.",
    "Review vocabulary and grammar consistently.",
    "Watch English videos to improve listening.",
    "Keep up the excellent work.",
  ],
};

const pdfOpts = (filename) => ({
  margin: 0,
  filename,
  image: { type: "jpeg", quality: 0.98 },
  html2canvas: { scale: 2, useCORS: true },
  jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
});

export default function StudentReport() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== "coordinator";
  const [d, setD] = useState(null);
  const [comment, setComment] = useState({ progress: "", strengths: "", areas: "", recommendation: "" });

  const load = useCallback(() => {
    api.get(`/students/${studentId}/report`).then((r) => {
      setD(r.data);
      setComment({ progress: "", strengths: "", areas: "", recommendation: "", ...r.data.comment });
    }).catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat"));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const saveComment = async () => {
    try { await api.put(`/students/${studentId}/comment`, comment); toast.success("Komentar tersimpan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const fileName = () => `Progress-Report-${(d.student.name || "siswa").replace(/\s+/g, "-")}-${(d.class.school_name || "").replace(/\s+/g, "-")}-Semester-${d.class.semester}-${d.class.academic_year}.pdf`;
  const downloadPDF = () => {
    const el = document.getElementById(`sheet-${studentId}`);
    html2pdf().set(pdfOpts(fileName())).from(el).save();
  };

  if (!d) return <div className="p-8 text-muted-foreground">Memuat...</div>;
  const sheet = { cls: d.class, academic: d.academic_components, nonAcademic: d.non_academic_components, scale: d.grading_scale, iecLogo: d.iec_logo, title: d.report_title, program: d.program_name, orgAddress: d.org_address, bmName: d.bm_name, bmSignature: d.bm_signature, student: d.student, scores: d.scores, comment };

  const addTemplate = (field, text) => setComment((p) => ({ ...p, [field]: p[field] ? `${p[field]} ${text}` : text }));

  return (
    <div className="space-y-4" data-testid="student-report-page">
      <Toaster position="top-center" richColors />
      <style>{`@media print { .no-print { display:none !important; } @page { size:A4 portrait; margin:0; } body{-webkit-print-color-adjust:exact;} .report-sheet{ box-shadow:none !important; } }`}</style>

      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="rounded-full gap-1" data-testid="report-back"><ArrowLeft className="w-4 h-4" /> Kembali</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="rounded-full gap-1" data-testid="report-print"><Printer className="w-4 h-4" /> Print</Button>
          <Button onClick={downloadPDF} className="rounded-full bg-gtsnavy text-white gap-1" data-testid="report-download-pdf"><Download className="w-4 h-4" /> Download PDF</Button>
        </div>
      </div>

      {canWrite && (
        <Card className="rounded-2xl border-none shadow-sm p-5 bg-white no-print" data-testid="comment-editor">
          <p className="font-nunito font-bold text-gtsnavy mb-3">Komentar Guru</p>
          {[
            { k: "progress", l: "Progress" },
            { k: "strengths", l: "Strengths" },
            { k: "areas", l: "Areas for Improvement" },
            { k: "recommendation", l: "Recommendation" },
          ].map((f) => (
            <div key={f.k} className="mb-3">
              <Label>{f.l}</Label>
              <textarea value={comment[f.k]} onChange={(e) => setComment({ ...comment, [f.k]: e.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 mt-1 px-3 py-2 text-sm focus:border-gtsorange focus:outline-none" data-testid={`comment-${f.k}`} />
              {TEMPLATES[f.k] && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {TEMPLATES[f.k].map((t, i) => <button key={i} type="button" onClick={() => addTemplate(f.k, t)} className="text-[11px] rounded-full bg-slate-100 hover:bg-slate-200 px-2 py-1" data-testid={`tmpl-${f.k}-${i}`}>+ {t.slice(0, 28)}…</button>)}
                </div>
              )}
            </div>
          ))}
          <Button onClick={saveComment} className="rounded-full bg-gtsorange text-white gap-1" data-testid="save-comment-btn"><Save className="w-4 h-4" /> Simpan Komentar</Button>
        </Card>
      )}

      <div className="overflow-x-auto flex justify-center bg-slate-100 rounded-2xl p-4 print:p-0 print:bg-white">
        <div className="shadow-lg print:shadow-none">
          <ReportSheet d={sheet} id={`sheet-${studentId}`} />
        </div>
      </div>
    </div>
  );
}
